import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    contentSourceApi, 
    sectionApi, 
    cmsUtils 
} from '../services/cmsApi';
import { getAllMetadataTypes } from '../services/adminApi';
import { useAuth } from '../context/AuthContext';
import { 
    ContentSource, 
    Section, 
    CreateContentSourceForm, 
    UpdateContentSourceForm, 
    CreateSectionForm, 
    UpdateSectionForm, 
    ReviewSectionForm, 
    AssignContentSourceForm, 
    ReorderSectionsForm,
    FilterState
} from '../types/cms';

// Content Sources hooks
export const useContentSources = (filters: Partial<FilterState> = {}) => {
    const { authState } = useAuth();
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Memoize filters object to prevent unnecessary re-fetches
    const memoizedFilters = useMemo(() => ({
        search: filters.search || '',
        status: filters.status || '',
        filter_tags: filters.filter_tags || [],
        assigned_to_id: filters.assigned_to_id || '',
        visibility: filters.visibility || '',
        review_status: filters.review_status || '',
        sortBy: filters.sortBy || 'updated_at',
        sortOrder: filters.sortOrder || 'DESC',
        page: filters.page || 1,
        limit: filters.limit || 20
    }), [
        filters.search,
        filters.status,
        filters.filter_tags,
        filters.assigned_to_id,
        filters.visibility,
        filters.review_status,
        filters.sortBy,
        filters.sortOrder,
        filters.page,
        filters.limit
    ]);

    const fetchData = useCallback(async () => {
        if (!authState.isAuthenticated || !authState.token) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await contentSourceApi.getAll(memoizedFilters);
            setData(response);
        } catch (err) {
            setError(err as Error);
        } finally {
            setIsLoading(false);
        }
    }, [memoizedFilters, authState.isAuthenticated, authState.token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchData,
    };
};

export const useContentSource = (id: string) => {
    const [data, setData] = useState<ContentSource | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        if (!id) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await contentSourceApi.getById(id);
            setData(response);
        } catch (err) {
            setError(err as Error);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Function to update the local state without refetching
    const updateData = useCallback((updatedData: ContentSource | null) => {
        setData(updatedData);
    }, []);

    return {
        data,
        isLoading,
        error,
        refetch: fetchData,
        updateData, // Expose setter to update state without refetch
    };
};

export const useCreateContentSource = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async (data: CreateContentSourceForm) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await contentSourceApi.create(data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useUpdateContentSource = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async ({ id, data }: { id: string; data: UpdateContentSourceForm }) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await contentSourceApi.update(id, data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useDeleteContentSource = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async (id: string) => {
        setIsLoading(true);
        setError(null);
        try {
            await contentSourceApi.delete(id);
            setIsLoading(false);
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useAssignContentSource = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async ({ id, data }: { id: string; data: AssignContentSourceForm }) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await contentSourceApi.assign(id, data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useExportContentSource = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async (id: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await contentSourceApi.export(id);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

// Sections hooks
export const useSections = (
    contentSourceId: string, 
    options: {
        depth?: number;
        parent_id?: string;
        ref_visibility?: string;
    } = {}
) => {
    const [data, setData] = useState<Section[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        if (!contentSourceId) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await sectionApi.getByContentSource(contentSourceId, options);
            setData(response);
        } catch (err) {
            setError(err as Error);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentSourceId, JSON.stringify(options)]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchData,
    };
};

export const useSection = (id: string) => {
    const [data, setData] = useState<Section | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        if (!id) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await sectionApi.getById(id);
            setData(response);
        } catch (err) {
            setError(err as Error);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchData,
    };
};

export const useCreateSection = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async ({ contentSourceId, data }: { contentSourceId: string; data: CreateSectionForm }) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await sectionApi.create(contentSourceId, data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useUpdateSection = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async ({ id, data }: { id: string; data: UpdateSectionForm }) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await sectionApi.update(id, data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useReviewSection = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async ({ id, data }: { id: string; data: ReviewSectionForm }) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await sectionApi.review(id, data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useDeleteSection = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async (id: string) => {
        setIsLoading(true);
        setError(null);
        try {
            await sectionApi.delete(id);
            setIsLoading(false);
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

export const useReorderSections = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const mutate = useCallback(async ({ contentSourceId, data }: { contentSourceId: string; data: ReorderSectionsForm }) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await sectionApi.reorder(contentSourceId, data);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err as Error);
            setIsLoading(false);
            throw err;
        }
    }, []);

    return {
        mutate,
        mutateAsync: mutate,
        isLoading,
        isPending: isLoading,
        error,
    };
};

// Utility hooks
export const useContentSourcePermissions = (contentSource: ContentSource | null, userRole: string) => {
    return {
        canEdit: cmsUtils.canPerformAction(userRole, 'edit_content_source', contentSource),
        canReview: cmsUtils.canPerformAction(userRole, 'review_content_source', contentSource),
        canDelete: cmsUtils.canPerformAction(userRole, 'delete_content_source', contentSource),
        canAssign: cmsUtils.canPerformAction(userRole, 'assign_content_source', contentSource),
        canCreateSection: cmsUtils.canPerformAction(userRole, 'create_section', contentSource),
        canReorderSections: cmsUtils.canPerformAction(userRole, 'reorder_sections', contentSource),
    };
};

export const useSectionPermissions = (section: Section | null, userRole: string) => {
    return {
        canEdit: cmsUtils.canPerformAction(userRole, 'edit_section', section),
        canReview: cmsUtils.canPerformAction(userRole, 'review_section', section),
        canDelete: cmsUtils.canPerformAction(userRole, 'delete_section', section),
    };
};

// Prefetch hooks (no-op without caching)
export const usePrefetchContentSource = () => {
    return (id: string) => {
        // No-op: prefetching not needed without cache
    };
};

export const usePrefetchSections = () => {
    return (contentSourceId: string, options: any = {}) => {
        // No-op: prefetching not needed without cache
    };
};

// Optimistic updates (no-op without caching)
export const useOptimisticUpdateSection = () => {
    return (id: string, data: UpdateSectionForm) => {
        // No-op: optimistic updates not available without cache
    };
};

// Background refetch (no-op without caching)
export const useBackgroundRefetch = () => {
    return () => {
        // No-op: background refetch not available without cache
    };
};

// Cache management (no-op without caching)
export const useCacheManagement = () => {
    return {
        clearAll: () => {},
        removeContentSource: (id: string) => {},
        removeSections: (contentSourceId: string) => {},
        invalidateContentSources: () => {},
        invalidateSections: (contentSourceId?: string) => {},
    };
};

// Metadata Types hook
export const useMetadataTypes = () => {
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getAllMetadataTypes();
            setData(response);
        } catch (err) {
            setError(err as Error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchData,
    };
};
